// Sprachnamen für die Auswahllisten der Self-Service-Fläche. Deutsch kommt
// unverändert aus den Daten (`languages_list` bzw. `LANGUAGES`); Englisch baut
// der Browser aus dem BCP-47-Code (`en-US` → „American English"). Kennt er den
// Code nicht oder ist er ungültig (`Intl.DisplayNames#of` wirft), bleibt das
// deutsche Label stehen — lieber deutsch als leer.
import { sortOptionsByLabel, type LabeledOption } from '@/lib/sortOptions';
import type { Lang } from './lang';

let displayNamesEn: Intl.DisplayNames | null | undefined;

function englishName(code: string): string | null {
  if (displayNamesEn === undefined) {
    try {
      displayNamesEn = new Intl.DisplayNames(['en'], { type: 'language' });
    } catch {
      displayNamesEn = null;
    }
  }
  if (!displayNamesEn) return null;
  try {
    const name = displayNamesEn.of(code);
    // Unbekannte, aber gültige Codes gibt `of` unverändert zurück → kein echter Name.
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
}

/** Anzeigename einer Sprache in der gewählten Oberflächensprache. */
export function languageName(code: string, germanLabel: string, lang: Lang): string {
  if (lang === 'de') return germanLabel;
  return englishName(code) ?? germanLabel;
}

// Marker, den `audioLanguageOptions` an einen Altwert hängt (deutsch, geteilt mit dem Admin).
const ALTWERT_DE = ' (Altwert)';

/**
 * Sprach-Optionen für ein Dropdown übersetzen. Deutsch unverändert; Englisch mit
 * englischen Namen, neu sortiert. Ein angehängter Altwert („xy (Altwert)") behält
 * seinen Code und bekommt den übersetzten Marker.
 */
export function localizeLanguageOptions(
  options: readonly LabeledOption[],
  lang: Lang,
  legacyMarker: string,
): LabeledOption[] {
  if (lang === 'de') return [...options];
  return sortOptionsByLabel(options.map((o) => (o.label.endsWith(ALTWERT_DE)
    ? { value: o.value, label: `${o.value} ${legacyMarker}` }
    : { value: o.value, label: languageName(o.value, o.label, lang) })));
}

/** Leerauswahl („Ohne Kategorie" …) mit übersetztem Label; übrige Optionen unverändert. */
export function withEmptyLabel(options: readonly LabeledOption[], label: string): LabeledOption[] {
  return options.map((o) => (o.value === '' ? { value: '', label } : o));
}
